import { captionSentence } from '@goaltree/backend/convex/lib/coach'
import { useConvexAuth } from 'convex/react'
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'

import { CheckinFrameAsk, SelfCameraPreview } from '@/components/checkin-frame-ask'
import { CoachCallPip } from '@/components/coach-call-pip'
import { CoachChatSheet } from '@/components/coach-chat-sheet'
import { useCoachCallSession } from '@/hooks/use-coach-call-session'
import { type CoachSheetRequest, useCoachCall } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'
import { dismissFrameAsk, stopSelfCamera } from '@/lib/self-camera'
import { useCaptureStore } from '@/store/capture.store'

let callNonce = 0

// Tailwind `lg` is min-width 1024, and the bottom nav is `lg:hidden`. Read it
// synchronously: a hook that starts false would flash the floating card.
function subscribePhone(onChange: () => void) {
  const mq = window.matchMedia('(max-width: 1023px)')
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

// The single app-wide coach sheet and its live call, mounted once at the root.
// Signing out unmounts it, which hangs up.
export function CoachCallHost() {
  const { isAuthenticated, isLoading } = useConvexAuth()
  if (!isAuthenticated && !isLoading) return null
  return <CoachCallHostInner />
}

function CoachCallHostInner() {
  const session = useCoachCallSession()
  const { voiceCall, callActive } = session
  const sheet = useCoachCall((s) => s.sheet)
  const mode = useCoachCall((s) => s.mode)
  const corner = useCoachCall((s) => s.corner)
  const bottomNav = useCoachCall((s) => s.bottomNav)
  const captureOpen = useCaptureStore((s) => s.open)
  const coaching = useIsCoaching()
  const phone = useSyncExternalStore(
    subscribePhone,
    () => window.matchMedia('(max-width: 1023px)').matches,
    () => false,
  )
  const coachingRef = useRef(coaching)
  coachingRef.current = coaching
  // The nav coach is the minimised face, so the floating card would sit on top of it.
  const showPill = callActive && mode === 'pip' && phone && bottomNav && !captureOpen && !coaching
  const controls = useRef({
    toggleMute: voiceCall.toggleMute,
    toggleVideo: () => void voiceCall.toggleVideo(),
    hangUp: session.hangUpFromPip,
  })
  controls.current.toggleMute = voiceCall.toggleMute
  controls.current.toggleVideo = () => void voiceCall.toggleVideo()
  controls.current.hangUp = session.hangUpFromPip

  useLayoutEffect(() => {
    if (!showPill) {
      if (useCoachCall.getState().navCall) useCoachCall.setState({ navCall: null })
      return
    }
    const next = {
      name: session.coachName,
      connecting: voiceCall.status === 'connecting',
      elapsedSeconds: voiceCall.elapsedSeconds,
      muted: voiceCall.muted,
      faceVideo: session.coachFaceOn && voiceCall.faceLive ? voiceCall.faceVideoRef.current : null,
      coachSpeaking: voiceCall.phase === 'speaking',
      caption:
        voiceCall.lastSpeaker === 'coach' && voiceCall.coachCaption
          ? captionSentence(voiceCall.coachCaption.text, voiceCall.coachCaption.chars).text
          : null,
      videoOn: voiceCall.videoOn,
      canVideo: session.coachFaceOn && voiceCall.status === 'live',
    }
    const prev = useCoachCall.getState().navCall
    if (
      prev &&
      prev.name === next.name &&
      prev.connecting === next.connecting &&
      prev.elapsedSeconds === next.elapsedSeconds &&
      prev.muted === next.muted &&
      prev.faceVideo === next.faceVideo &&
      prev.coachSpeaking === next.coachSpeaking &&
      prev.caption === next.caption &&
      prev.videoOn === next.videoOn &&
      prev.canVideo === next.canVideo
    ) {
      return
    }
    useCoachCall.setState({
      navCall: {
        ...next,
        toggleMute: () => controls.current.toggleMute(),
        toggleVideo: () => controls.current.toggleVideo(),
        hangUp: () => controls.current.hangUp(),
      },
    })
  }, [
    showPill,
    session.coachName,
    session.coachFaceOn,
    voiceCall.status,
    voiceCall.faceLive,
    voiceCall.phase,
    voiceCall.elapsedSeconds,
    voiceCall.muted,
    voiceCall.lastSpeaker,
    voiceCall.coachCaption?.text,
    voiceCall.coachCaption?.chars,
    voiceCall.videoOn,
  ])
  useLayoutEffect(() => () => useCoachCall.setState({ navCall: null }), [])

  // Every coach trigger (headers, FABs, playbooks, goal details, the ring) opens it via this event.
  useEffect(() => {
    const open = (e: Event) => {
      // The AI coach belongs to the personal side.
      if (coachingRef.current) return
      const detail = (e as CustomEvent<CoachSheetRequest | undefined>).detail
      // A fresh nonce so a tap still starts a call when the chat sheet is already open.
      const req = detail?.startCall ? { ...detail, callNonce: ++callNonce } : detail
      useCoachCall.getState().openSheet(req)
    }
    window.addEventListener('open-coach', open)
    return () => window.removeEventListener('open-coach', open)
  }, [])
  useEffect(() => {
    if (!callActive) {
      stopSelfCamera()
      dismissFrameAsk()
    }
    return () => {
      stopSelfCamera()
      dismissFrameAsk()
    }
  }, [callActive])
  useEffect(() => {
    if (!coaching) return
    if (useCoachCall.getState().active) voiceCall.end()
    useCoachCall.getState().closeSheet()
  }, [coaching]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <CoachChatSheet
        open={sheet.open}
        onClose={() => {
          const st = useCoachCall.getState()
          if (st.active) st.minimize()
          else st.closeSheet()
        }}
        initialMessage={sheet.message}
        playbookContext={sheet.playbookContext}
        startCall={sheet.startCall}
        callVideo={sheet.callVideo}
        callNonce={sheet.callNonce}
        session={session}
      />
      {/* Simli's lip-synced voice plays here, so it keeps going whichever view is up. */}
      <audio ref={voiceCall.faceAudioRef} autoPlay className="hidden" />
      <CheckinFrameAsk />
      {callActive && mode === 'pip' && (
        <SelfCameraPreview className="pointer-events-none fixed bottom-36 left-4 z-[70] size-24" />
      )}
      {/* The pill has no face of its own. Keep Simli's element mounted, the way the card did. */}
      {callActive && mode === 'pip' && !showPill && (
        <CoachCallPip
          call={voiceCall}
          voice={session.coachVoice}
          name={session.coachName}
          corner={corner}
          onCorner={useCoachCall.getState().setCorner}
          onExpand={useCoachCall.getState().expand}
          onHangUp={session.hangUpFromPip}
        />
      )}
    </>
  )
}
